import React, { memo, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import { ButtonBase, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import { parseTitle } from '@utils/index';
import classes from '@pages/Deals/components/styles';
import { DealCardProps } from '@pages/Deals/components/DealCard';

const EmblaCarousel = ({
  images,
  title,
  imageLoading
}: {
  imageLoading: 'lazy' | 'eager';
  title: string;
  images: string[];
}) => {
  const randomDelayValues = [2000, 2150, 2300, 2500, 2800, 3000];
  const randomDelay =
    randomDelayValues[Math.floor(Math.random() * randomDelayValues.length)];
  // Keep a stable plugin instance
  const autoplay = useRef(
    Autoplay({
      delay: randomDelay,
      stopOnInteraction: false,
      stopOnMouseEnter: false
    })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', startIndex: 0 },
    [autoplay.current]
  );

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.scrollTo(0, true); // ensure we start on slide 1
  }, [emblaApi]);

  return (
    <Box sx={classes.embla} ref={emblaRef}>
      <Box sx={classes.emblaContainer}>
        {images.map((img) => (
          <Box key={img} sx={classes.emblaSlide}>
            <Box
              component="img"
              alt={parseTitle(title)}
              src={img?.replace('http:', 'https:')}
              loading={imageLoading}
              sx={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'contain', // contain inside 140x140 without distortion
                borderRadius: '3px'
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const DealCardImage = memo((props: DealCardProps) => {
  const { title, image, image240Url, index, haulImage } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const imageLoading =
    (isMobile && index > 3) || (!isMobile && index > 11) ? 'lazy' : 'eager';

  const image240UrlWebp = image240Url?.replace('.jpg', '.webp');
  if (haulImage && image240UrlWebp) {
    return (
      <ButtonBase sx={classes.imageWrapper}>
        <EmblaCarousel
          imageLoading={imageLoading}
          title={title}
          images={[image240UrlWebp, haulImage]}
        />
      </ButtonBase>
    );
  }

  return (
    <ButtonBase sx={classes.imageWrapper}>
      <Box
        sx={{
          height: 140,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '3px',
          paddingLeft: '4px'
        }}
      >
        <Box
          component="img"
          alt={parseTitle(title)}
          src={(image240UrlWebp || image)?.replace('http:', 'https:')}
          sx={classes.imageMobile}
          loading={imageLoading}
          role="presentation"
        />
      </Box>
    </ButtonBase>
  );
});

export default DealCardImage;
